/* eslint-disable max-lines */
import React, { useEffect, useState } from 'react';
import renderRecaptcha, { resetRecaptcha } from './renderRecaptcha';
import { reportError } from 'src/dataLayer/remoteData/rollbar/reporter';
import { useRouteParamsContext } from 'src/viewLayer/components/contexts/RouteParamsContext';
import './index.scss';
import { t } from 'src/viewLayer/helpers/translations';

const RECAPTCHA_TARGET_ID = 'RECAPTCHA_TARGET_ID';
const CAPTCHA_ERROR_KEY = 'common.error.unknown';

let captchaToken;
let storedSessionCode;
let recaptchaWidgetID;

export const extractCaptchaKey = ({ captchaConfig }) =>
  captchaConfig ? captchaConfig.params.site_key : null;

const onCaptchaResponse = (responseToken, setCaptchaComplete) => {
  captchaToken = responseToken;
  setCaptchaComplete(true);
};

const onCaptchaError = (error, setCaptchaComplete, setShowErrorMessage) => {
  reportError(error);
  recaptchaWidgetID = undefined;
  setCaptchaComplete(false);
  setShowErrorMessage(true);
};

const loadRecaptcha = (props, locale, showErrorMessage, setShowErrorMessage) => {
  if (showErrorMessage) return;
  if (recaptchaWidgetID !== undefined) return;
  storedSessionCode = props.sessionCode;

  const setCaptchaComplete = props.setCaptchaComplete;
  const onSuccess = (responseToken) => onCaptchaResponse(responseToken, setCaptchaComplete);
  const onError = (error) => onCaptchaError(error, setCaptchaComplete, setShowErrorMessage);
  const onExpired = () => setCaptchaComplete(false);

  const recaptchaArgs = {
    targetId: RECAPTCHA_TARGET_ID,
    captchaKey: extractCaptchaKey(props),
    locale,
    onSuccess,
    onError,
    onExpired
  };

  recaptchaWidgetID = renderRecaptcha(recaptchaArgs);
};

const reloadRecaptcha = (props) => {
  resetRecaptcha();
  storedSessionCode = props.sessionCode;
};

const RecaptchaWidget = (props) => {
  const { locale } = useRouteParamsContext();
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  // Reset the widget ID when the component unmounts
  useEffect(() => {
    return () => recaptchaWidgetID = undefined;
  }, []);

  useEffect(() => {
    // If the session has changed, reset the widget
    // This block must run after rendering the component, to stop captcha trying to render before the container is there
    (recaptchaWidgetID === undefined || props.sessionCode === storedSessionCode)
      ? loadRecaptcha(props, locale, showErrorMessage, setShowErrorMessage)
      : reloadRecaptcha(props);
  }, [props, locale, showErrorMessage]);

  return (
    <a>
        <div id={ RECAPTCHA_TARGET_ID } />
        { showErrorMessage && <div className="recaptchaError">{t(CAPTCHA_ERROR_KEY)}</div> }
    </a>
  );
};

const RecaptchaContainer = ({ children }) => (
  <div className="recaptchaContainer">
    { children }
  </div>
);

export const getCaptchaResult = () => captchaToken ? ({ response: captchaToken }) : {};

// Render an empty container if user must not submit a captcha (for UI spacing purposes).
export default (props) => {
  const userMustSubmitRecaptcha = Boolean(extractCaptchaKey(props));
  if (userMustSubmitRecaptcha) {
    return (
      <RecaptchaContainer>
        <RecaptchaWidget {...props} />
      </RecaptchaContainer>
    );
  }
  // New design removes the empty spaces
  return null;
};
